/**
 * Payment Info Section Component
 * Matches Flutter's blade_screen.dart Section 2: Payment Info
 *
 * Fields:
 * - Row 1: Payment ID | Last 4 Digits | Settlement Value
 * - Row 2: Auth Time | Settlement Time
 * - Extra: Currency, Calculation Method (from API)
 *
 * Collapsed by default when session has no stop time (active session)
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/core/components/ui/collapsible';
import { SessionDetail } from '../../types/session-types';
import { formatTime } from '../../utils/date-utils';

interface PaymentInfoSectionProps {
  detail: SessionDetail;
}

export function PaymentInfoSection({ detail }: PaymentInfoSectionProps) {
  const isActive = !detail.stopTime;
  const [isOpen, setIsOpen] = useState(!isActive);

  const formatCurrency = (
    value: number | null | undefined,
    currency: string | null | undefined
  ): string => {
    if (value === null || value === undefined) return '--';
    const symbol = currency || '';
    return `${symbol} ${value.toFixed(2)}`.trim();
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="border border-gray-200 rounded-lg"
    >
      <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-lg">
        <h3 className="text-sm font-semibold text-gray-900">Payment Info</h3>
        <div className="flex items-center gap-2">
          {isActive && !isOpen && (
            <span className="text-xs text-gray-400">Not yet available</span>
          )}
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-4 pb-4 pt-1">
          {/* Row 1: Payment ID | Last 4 Digits | Settlement Value */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <InfoItem
              label="Payment ID"
              value={detail.nayaxTransactionId?.toString() ?? '--'}
            />
            <InfoItem label="Last 4 Digits" value={detail.last4Digits ?? '--'} />
            <InfoItem
              label="Settlement Value"
              value={formatCurrency(detail.settlementValue, detail.currencyName)}
            />
          </div>

          {/* Row 2: Auth Time | Settlement Time */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <InfoItem label="Auth Time" value={formatTime(detail.authTime)} />
            <InfoItem
              label="Settlement Time"
              value={formatTime(detail.settlementTime)}
              className="col-span-2"
            />
          </div>

          {/* Extra from API: Currency, Calculation Method */}
          <div className="grid grid-cols-3 gap-4">
            {detail.currencyName && (
              <InfoItem label="Currency" value={detail.currencyName} />
            )}
            {detail.calculationMethod && (
              <InfoItem label="Calculation Method" value={detail.calculationMethod} />
            )}
            {detail.vatRate !== null && detail.vatRate !== undefined && (
              <InfoItem label="VAT Rate" value={`${detail.vatRate}%`} />
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface InfoItemProps {
  label: string;
  value: string;
  className?: string;
}

function InfoItem({ label, value, className = '' }: InfoItemProps) {
  return (
    <div className={className}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}
